// Noise Lab review: judge one spot at a time — listen before/after, look at the spectral picture, answer
// with the keyboard. Every answer is saved as it is given and becomes a training label.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import type { LabReviewDetail, LabReviewOptions, LabSpot } from "../api/types";
import { Spectrogram } from "../components/Spectrogram";
import { useMe } from "../lib/me";
import { playExcerpt, stopPlayback, wavExcerpt } from "../lib/wavRange";
import type { Excerpt } from "../lib/wavRange";

const BASE = "/api/modules/noise_lab";
const HALF = 1.5;             // seconds either side of the spot
const URL_TTL_MS = 30 * 60 * 1000;

const KIND_LABEL: Record<string, string> = {
  "mouth-like": "mouth noise", "LF event": "low thump", "long low-level edit": "long clean-up", "artefact": "tiny artefact",
};
const CTX_LABEL: Record<string, string> = { "pause": "in a pause", "breath/low-level": "in a breath", "under speech": "under speech" };
const VERDICTS: { id: string; key: string; label: string; hint: string }[] = [
  { id: "yes", key: "Y", label: "Yes", hint: "a noise that needs fixing" },
  { id: "no", key: "N", label: "No", hint: "not a noise, or leave it" },
  { id: "borderline", key: "B", label: "Borderline", hint: "could go either way" },
];
const GRAINS: { id: string; key: string; label: string; hint: string }[] = [
  { id: "coarse", key: "C", label: "Coarse", hint: "distracting: flag it even on a quick pass" },
  { id: "medium", key: "M", label: "Medium", hint: "fix it on a normal clean" },
  { id: "fine", key: "F", label: "Fine", hint: "only on a thorough, fine-comb clean" },
];

function clock(s: number) {
  const m = Math.floor(s / 60);
  return `${m}:${(s - m * 60).toFixed(2).padStart(5, "0")}`;
}
const answered = (s: LabSpot) => !!(s.verdict || s.noise_type || s.grain);

type Clip = { before?: Excerpt; after?: Excerpt; error?: string };

export function NoiseLabReview() {
  const { setId = "", reviewId = "" } = useParams();
  const { data: me } = useMe();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["lab-review", reviewId],
    queryFn: () => api<LabReviewDetail>(`${BASE}/sets/${setId}/reviews/${reviewId}`),
    enabled: !!me?.is_superadmin,
    staleTime: Infinity,
  });
  const opts = useQuery({
    queryKey: ["lab-review-options"],
    queryFn: () => api<LabReviewOptions>(`${BASE}/review-options`),
    enabled: !!me?.is_superadmin,
    staleTime: Infinity,
  });
  const [spots, setSpots] = useState<LabSpot[]>([]);
  const [idx, setIdx] = useState(0);
  const [clips, setClips] = useState<Record<string, Clip>>({});
  const [saving, setSaving] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveErr, setSaveErr] = useState("");
  const [autoplay, setAutoplay] = useState(() => {
    try { return localStorage.getItem("lab-autoplay") !== "0"; } catch { return true; }
  });
  const [playing, setPlaying] = useState<"before" | "after" | null>(null);
  // a noise the reviewer has marked on a picture but not yet added: where, and on which recording
  const [pending, setPending] = useState<{ side: "before" | "after"; t: number; dur: number } | null>(null);
  const [adding, setAdding] = useState(false);
  const urls = useRef(new Map<string, { url: Promise<string>; at: number }>());
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const savedNotes = useRef(new Map<string, string>());
  const started = useRef(false);

  useEffect(() => {
    if (q.data && !started.current) {
      started.current = true;
      setSpots(q.data.spots);
      const first = q.data.spots.findIndex((s) => !answered(s));
      setIdx(first >= 0 ? first : 0);
    }
  }, [q.data]);

  const review = q.data;
  const spot = spots[idx];
  const calibration = review?.kind === "calibration";
  const types = opts.data?.noise_types ?? [];

  const urlFor = useCallback((pairId: string, side: "before" | "after"): Promise<string> => {
    const k = `${pairId}:${side}`;
    const hit = urls.current.get(k);
    if (hit && Date.now() - hit.at < URL_TTL_MS) return hit.url;
    // one request per recording, shared by the spot on screen and the one being preloaded
    const url = api<{ url: string }>(`${BASE}/sets/${setId}/pairs/${pairId}/files/${side}/url`).then((r) => r.url);
    urls.current.set(k, { url, at: Date.now() });
    url.catch(() => urls.current.delete(k));
    return url;
  }, [setId]);

  const load = useCallback(async (s: LabSpot) => {
    if (clips[s.id]) return;
    setClips((c) => ({ ...c, [s.id]: {} }));
    try {
      const bUrl = await urlFor(s.pair_id, "before");
      const before = await wavExcerpt(bUrl, s.t - HALF, s.t + HALF, `${s.pair_id}:before`);
      setClips((c) => ({ ...c, [s.id]: { ...c[s.id], before } }));
      if (!calibration && s.t_after != null) {
        const aUrl = await urlFor(s.pair_id, "after");
        const after = await wavExcerpt(aUrl, s.t_after - HALF, s.t_after + HALF, `${s.pair_id}:after`);
        setClips((c) => ({ ...c, [s.id]: { ...c[s.id], after } }));
      }
    } catch (e) {
      setClips((c) => ({ ...c, [s.id]: { ...c[s.id], error: (e as Error).message } }));
    }
  }, [clips, urlFor, calibration]);

  // load this spot and the next one; autoplay when this one's audio arrives
  useEffect(() => {
    if (!spot) return;
    void load(spot);
    const nx = spots[idx + 1];
    if (nx) void load(nx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spot?.id]);

  const play = useCallback((side: "before" | "after") => {
    if (!spot) return;
    const ex = clips[spot.id]?.[side];
    if (!ex) return;
    if (playing === side) { stopPlayback(); setPlaying(null); return; }
    playExcerpt(ex, `${spot.id}:${side}`, 0, () => setPlaying((p) => (p === side ? null : p)));
    setPlaying(side);
  }, [spot, clips, playing]);

  const autoPlayed = useRef<string | null>(null);
  useEffect(() => {
    if (!spot || !autoplay || autoPlayed.current === spot.id) return;
    const ex = clips[spot.id]?.before;
    if (ex) {
      autoPlayed.current = spot.id;
      playExcerpt(ex, `${spot.id}:before`, 0, () => setPlaying((p) => (p === "before" ? null : p)));
      setPlaying("before");
    }
  }, [spot, clips, autoplay]);

  useEffect(() => () => stopPlayback(), []);

  const go = useCallback((to: number) => {
    stopPlayback();
    setPlaying(null);
    setPending(null);
    setIdx(Math.max(0, Math.min(spots.length - 1, to)));
  }, [spots.length]);

  // clicking a picture plays from just before that point and leaves a marker there
  const pick = useCallback((side: "before" | "after", t: number, dur: number) => {
    if (!spot) return;
    setPending({ side, t, dur });
    const ex = clips[spot.id]?.[side];
    if (!ex) return;
    playExcerpt(ex, `${spot.id}:${side}`, Math.max(0, t - 0.3 - ex.t0), () => setPlaying((p) => (p === side ? null : p)));
    setPlaying(side);
  }, [spot, clips]);

  const addPending = useCallback(async () => {
    if (!spot || !pending || adding) return;
    setAdding(true);
    setSaving("saving");
    try {
      const made = await api<LabSpot>(`${BASE}/sets/${setId}/reviews/${reviewId}/spots`, {
        method: "POST",
        body: JSON.stringify({ from_spot_id: spot.id, t: pending.t, dur: pending.dur, timeline: pending.side }),
      });
      setSpots((ss) => [...ss.slice(0, idx + 1), made, ...ss.slice(idx + 1)]);
      setSaving("saved");
      setPending(null);
      stopPlayback();
      setPlaying(null);
      setIdx(idx + 1);                    // straight to it, to set its type and how much it matters
      qc.invalidateQueries({ queryKey: ["lab-reviews", setId] });
    } catch (e) {
      setSaving("error");
      setSaveErr((e as Error).message);
    } finally {
      setAdding(false);
    }
  }, [spot, pending, adding, setId, reviewId, idx, qc]);

  const removeAdded = useCallback(async () => {
    if (!spot || spot.source !== "reviewer") return;
    try {
      await api(`${BASE}/sets/${setId}/reviews/${reviewId}/spots/${spot.id}`, { method: "DELETE" });
      const id = spot.id;
      setSpots((ss) => ss.filter((s) => s.id !== id));
      stopPlayback();
      setPlaying(null);
      setIdx((i) => Math.max(0, i - 1));
      qc.invalidateQueries({ queryKey: ["lab-reviews", setId] });
    } catch (e) {
      setSaving("error");
      setSaveErr((e as Error).message);
    }
  }, [spot, setId, reviewId, qc]);

  const nextUnanswered = useCallback(() => {
    for (let k = 1; k <= spots.length; k++) {
      const j = (idx + k) % spots.length;
      if (!answered(spots[j])) { go(j); return; }
    }
  }, [spots, idx, go]);

  const save = useCallback(async (patch: Partial<Pick<LabSpot, "verdict" | "noise_type" | "grain" | "note">>) => {
    if (!spot) return;
    const id = spot.id;
    setSpots((ss) => ss.map((s) => (s.id === id ? { ...s, ...patch } : s)));
    setSaving("saving");
    try {
      await api(`${BASE}/sets/${setId}/reviews/${reviewId}/spots/${id}`, { method: "PATCH", body: JSON.stringify(patch) });
      setSaving("saved");
      qc.invalidateQueries({ queryKey: ["lab-reviews", setId] });
    } catch (e) {
      setSaving("error");
      setSaveErr((e as Error).message);
    }
  }, [spot, setId, reviewId, qc]);

  const toggle = useCallback((field: "verdict" | "noise_type" | "grain", value: string) => {
    if (!spot) return;
    void save({ [field]: spot[field] === value ? "" : value });
  }, [spot, save]);

  // keyboard
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (el && (el.tagName === "TEXTAREA" || el.tagName === "INPUT" || el.tagName === "SELECT")) {
        if (e.key === "Escape") el.blur();
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (k === " ") { e.preventDefault(); play("before"); return; }
      if (k === "a" && !calibration) { play("after"); return; }
      if (k === "escape") { if (pending) setPending(null); else { stopPlayback(); setPlaying(null); } return; }
      if (k === "p") { void addPending(); return; }
      if (k === "arrowright" || k === "enter") { e.preventDefault(); go(idx + 1); return; }
      if (k === "arrowleft") { e.preventDefault(); go(idx - 1); return; }
      if (k === "u") { nextUnanswered(); return; }
      if (k === "e") { e.preventDefault(); noteRef.current?.focus(); return; }
      const v = VERDICTS.find((x) => x.key.toLowerCase() === k);
      if (v) { toggle("verdict", v.id); return; }
      const g = GRAINS.find((x) => x.key.toLowerCase() === k);
      if (g) { toggle("grain", g.id); return; }
      const n = parseInt(k, 10);
      if (n >= 1 && n <= types.length) toggle("noise_type", types[n - 1].id);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [play, go, idx, nextUnanswered, toggle, types, calibration, pending, addPending]);

  const done = useMemo(() => spots.filter(answered).length, [spots]);

  if (me && !me.is_superadmin) return <Navigate to="/" replace />;
  if (q.isError) return <div className="page lab-error">Couldn't load this review: {(q.error as Error).message}</div>;
  if (!review || !spot) return <div className="page muted">Loading…</div>;
  const pair = review.pairs[spot.pair_id];
  const clip = clips[spot.id] ?? {};
  const meta = spot.meta || {};

  return (
    <div className="page admin-page lab-page rv-page">
      <div className="page-head rv-head">
        <div>
          <Link to={`/admin/noise-lab/${setId}`} className="lab-back">Back to the set</Link>
          <h1>{review.name}</h1>
          <div className="stat-chips">
            <span className="stat-chip">Spot {idx + 1} of {spots.length}</span>
            <span className="stat-chip">{done} answered</span>
            <span className={`stat-chip rv-save rv-save-${saving}`}>
              {saving === "saving" ? "Saving…" : saving === "saved" ? "Saved" : saving === "error" ? `Not saved: ${saveErr}` : ""}
            </span>
          </div>
        </div>
        <label className="rv-autoplay">
          <input type="checkbox" checked={autoplay} onChange={(e) => {
            setAutoplay(e.target.checked);
            try { localStorage.setItem("lab-autoplay", e.target.checked ? "1" : "0"); } catch { /* private window */ }
          }} /> Play each spot when it opens
        </label>
      </div>

      <div className="progress-track rv-progress"><div className="progress-fill" style={{ width: `${(done / Math.max(1, spots.length)) * 100}%` }} /></div>

      <div className="rv-grid">
        <section className="card rv-listen">
          <div className="rv-where">
            <strong>{pair?.stem ?? "?"}</strong>
            <span className="muted"> at {clock(spot.t)}</span>
            {calibration ? (
              <span className="rv-hint muted">Hints are hidden in a calibration review. Judge by ear at your normal level.</span>
            ) : (
              <span className="rv-hint">
                {spot.source === "edit" && <>Your edit: {KIND_LABEL[meta.kind as string] ?? meta.kind}{meta.ctx ? `, ${CTX_LABEL[meta.ctx as string] ?? meta.ctx}` : ""}</>}
                {spot.source === "model" && <>Flagged by a model{typeof meta.p === "number" ? `, confidence ${(meta.p as number).toFixed(2)}` : ""}. You left it untouched.</>}
                {spot.source === "reviewer" && <>Added by you while judging the spot before{meta.marked_on === "after" ? ", marked on the recording after the edit" : ""}.</>}
                {meta.marker ? <span className="rv-marker"> · Your marker: “{(meta.marker as { name: string }).name}{(meta.marker as { description?: string }).description ? `: ${(meta.marker as { description: string }).description}` : ""}”</span> : null}
              </span>
            )}
          </div>
          {clip.error && <div className="lab-error">{clip.error}</div>}
          <div className="rv-spec">
            <Spectrogram excerpt={clip.before ?? null} playId={`${spot.id}:before`} markT={spot.t} markDur={spot.dur}
                         label={calibration ? "The recording" : "Before the edit"}
                         onPick={(tt, d) => pick("before", tt, d)} pending={pending?.side === "before" ? pending : null} />
            <button className={`btn sm${playing === "before" ? " primary" : ""}`} onClick={() => play("before")} disabled={!clip.before}>
              {playing === "before" ? "Stop" : "Play"} <kbd>Space</kbd>
            </button>
          </div>
          {!calibration && spot.t_after != null && (
            <div className="rv-spec">
              <Spectrogram excerpt={clip.after ?? null} playId={`${spot.id}:after`} markT={spot.t_after} markDur={spot.dur}
                           label="After the edit"
                           onPick={(tt, d) => pick("after", tt, d)} pending={pending?.side === "after" ? pending : null} />
              <button className={`btn sm${playing === "after" ? " primary" : ""}`} onClick={() => play("after")} disabled={!clip.after}>
                {playing === "after" ? "Stop" : "Play"} <kbd>A</kbd>
              </button>
            </div>
          )}
          {pending ? (
            <div className="rv-pending">
              <span>
                New spot at <strong>{clock(pending.t + pending.dur / 2)}</strong>
                {pending.dur > 0 && <> ({Math.round(pending.dur * 1000)} ms)</>} on the recording {pending.side === "after" ? "after" : "before"} the edit
              </span>
              <span className="rv-pending-actions">
                <button className="btn sm primary" onClick={() => void addPending()} disabled={adding}>Add as a new spot <kbd>P</kbd></button>
                <button className="btn sm ghost" onClick={() => setPending(null)}>Cancel <kbd>Esc</kbd></button>
              </span>
            </div>
          ) : (
            <div className="rv-pick-hint muted">
              Hear another noise here? Click where it is in a picture to play from there, or drag across a longer one, then press <kbd>P</kbd> to add it as a new spot.
            </div>
          )}
          {spot.source === "reviewer" && (
            <div><button className="btn sm ghost lab-danger" onClick={() => void removeAdded()}>Remove this spot</button></div>
          )}
          <div className="rv-strip" aria-label="All spots">
            {spots.map((s, i) => (
              <button key={s.id} className={`rv-dot${i === idx ? " cur" : ""}${answered(s) ? ` ans-${s.verdict || "set"}` : ""}${s.source === "reviewer" ? " added" : ""}`}
                      title={`${review.pairs[s.pair_id]?.stem ?? ""} ${clock(s.t)}${s.source === "reviewer" ? " (added by you)" : answered(s) ? " (answered)" : ""}`}
                      onClick={() => go(i)} />
            ))}
          </div>
        </section>

        <section className="card rv-answer">
          <div className="rv-q">Is there a noise here that needs fixing?</div>
          <div className="rv-opts">
            {VERDICTS.map((v) => (
              <button key={v.id} className={`rv-opt rv-v-${v.id}${spot.verdict === v.id ? " on" : ""}`} onClick={() => toggle("verdict", v.id)} title={v.hint}>
                <kbd>{v.key}</kbd> {v.label}
              </button>
            ))}
          </div>

          <div className="rv-q">What kind of noise?</div>
          <div className="rv-opts rv-types">
            {types.map((t, i) => (
              <button key={t.id} className={`rv-opt${spot.noise_type === t.id ? " on" : ""}`} onClick={() => toggle("noise_type", t.id)}>
                <kbd>{i + 1}</kbd> {t.label}
              </button>
            ))}
          </div>

          <div className="rv-q">How much does it matter?</div>
          <div className="rv-opts rv-grains">
            {GRAINS.map((g) => (
              <button key={g.id} className={`rv-opt${spot.grain === g.id ? " on" : ""}`} onClick={() => toggle("grain", g.id)}>
                <kbd>{g.key}</kbd> <span><strong>{g.label}</strong> <span className="muted">{g.hint}</span></span>
              </button>
            ))}
          </div>

          <div className="rv-q">Note <span className="muted">(optional, <kbd>E</kbd>)</span></div>
          <textarea ref={noteRef} className="input rv-note" rows={2} value={spot.note}
                    onChange={(e) => setSpots((ss) => ss.map((s) => (s.id === spot.id ? { ...s, note: e.target.value } : s)))}
                    onBlur={(e) => {
                      const was = savedNotes.current.get(spot.id) ?? (q.data?.spots.find((s) => s.id === spot.id)?.note ?? "");
                      if (e.target.value !== was) { savedNotes.current.set(spot.id, e.target.value); void save({ note: e.target.value }); }
                    }} />

          <div className="rv-nav">
            <button className="btn sm" onClick={() => go(idx - 1)} disabled={idx === 0}><kbd>←</kbd> Back</button>
            <button className="btn sm ghost" onClick={nextUnanswered} disabled={done === spots.length} title="Jump to the next spot without an answer">
              Unanswered <kbd>U</kbd>
            </button>
            <button className="btn sm primary" onClick={() => go(idx + 1)} disabled={idx === spots.length - 1}>Next <kbd>Enter</kbd></button>
          </div>
          {done === spots.length && <div className="rv-finished">Every spot in this review is answered.</div>}
        </section>
      </div>
    </div>
  );
}
